import type { CachedSnapshot, Snapshot, SnapshotSource } from './snapshotTypes'
import type { Repo } from './repo'

const META_SNAPSHOT = 'snapshot'

/**
 * Where the snapshot comes from.
 *
 * VITE_SNAPSHOT_URL points at the deployed backend. Until that exists, the app
 * falls back to a sample generated from real PlayHQ data
 * (npm run snapshot:sample) so fixture detection can be tested on the iPad. The
 * source is surfaced in the UI -- the app should never quietly imply that sample
 * data is live.
 */
const SNAPSHOT_URL: string | undefined = import.meta.env.VITE_SNAPSHOT_URL

/**
 * Supabase's publishable key (sb_publishable_...), the replacement for the legacy
 * anon JWT. Designed to be public: it identifies the project and grants nothing
 * on its own, because every table has RLS enabled with no policies.
 *
 * It is NOT a JWT, so it cannot satisfy an edge function's built-in JWT check --
 * sent as `Authorization: Bearer` it would be parsed as a token and rejected.
 * It goes in the `apikey` header only, and `snapshot` is deployed with
 * --no-verify-jwt to match. See amendment 02.5.
 *
 * The SECRET key (sb_secret_... / service_role) must NEVER appear here: every
 * VITE_ variable is bundled into the JavaScript shipped to the iPad.
 */
const PUBLISHABLE_KEY: string | undefined = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY

const SAMPLE_URL = './snapshot.sample.json'

/** Gym wifi is bad. Fail fast and fall back to cache rather than hang on setup. */
const FETCH_TIMEOUT_MS = 6000

export function snapshotUrl(): string {
  return SNAPSHOT_URL || SAMPLE_URL
}

export function isSampleData(): boolean {
  return !SNAPSHOT_URL
}

async function fetchWithTimeout(url: string, etag: string | null): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  const headers: Record<string, string> = {}
  if (etag) headers['if-none-match'] = etag
  // Only sent to the real backend; the bundled sample is a plain static file.
  // apikey only -- deliberately no Authorization bearer, see the note above.
  if (PUBLISHABLE_KEY && SNAPSHOT_URL) {
    headers.apikey = PUBLISHABLE_KEY
  }
  try {
    return await fetch(url, { signal: controller.signal, headers, cache: 'no-cache' })
  } finally {
    clearTimeout(timer)
  }
}

export async function readCachedSnapshot(repo: Repo): Promise<CachedSnapshot | null> {
  const cached = await repo.getMeta<CachedSnapshot>(META_SNAPSHOT)
  return cached ?? null
}

export interface RefreshResult {
  cached: CachedSnapshot | null
  /** Set when the network attempt failed; the cached copy is still returned. */
  error: string | null
  /** True when the server said 304 and the cached copy is already current. */
  notModified: boolean
}

/**
 * Fetch the snapshot and cache it. Offline-first: a network failure is never
 * fatal, it just leaves the previous cached copy in place. Callers should treat
 * a non-null `error` alongside a non-null `cached` as "working, but stale".
 */
export async function refreshSnapshot(repo: Repo): Promise<RefreshResult> {
  const previous = await readCachedSnapshot(repo)

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { cached: previous, error: 'Offline', notModified: false }
  }

  try {
    const res = await fetchWithTimeout(snapshotUrl(), previous?.etag ?? null)

    if (res.status === 304 && previous) {
      const refreshed: CachedSnapshot = { ...previous, fetchedAt: Date.now() }
      await repo.setMeta(META_SNAPSHOT, refreshed)
      return { cached: refreshed, error: null, notModified: true }
    }
    if (!res.ok) {
      return { cached: previous, error: `Snapshot fetch failed (HTTP ${res.status})`, notModified: false }
    }

    const snapshot = (await res.json()) as Snapshot
    const problem = validateSnapshot(snapshot)
    if (problem) return { cached: previous, error: problem, notModified: false }

    const source: SnapshotSource = isSampleData() ? 'sample' : 'network'
    const next: CachedSnapshot = {
      snapshot,
      source,
      fetchedAt: Date.now(),
      etag: res.headers.get('etag'),
    }
    await repo.setMeta(META_SNAPSHOT, next)
    return { cached: next, error: null, notModified: false }
  } catch (err) {
    return { cached: previous, error: describeFetchFailure(err), notModified: false }
  }
}

/**
 * Whatever went wrong, the scorer sees this on the setup screen. "TypeError:
 * Failed to fetch" tells them nothing and looks broken; "Offline" tells them
 * everything they need, because the app carries on from cache either way.
 */
function describeFetchFailure(err: unknown): string {
  if (err instanceof DOMException && err.name === 'AbortError') return 'Slow connection'
  // A network-level failure surfaces as a bare TypeError with no status.
  if (err instanceof TypeError) return 'Offline'
  const message = err instanceof Error ? err.message : String(err)
  return /failed to fetch|networkerror|load failed/i.test(message) ? 'Offline' : message
}

/**
 * Enough of a check that a truncated or wrong-shaped body never overwrites a
 * good cached snapshot mid-season.
 */
function validateSnapshot(value: unknown): string | null {
  if (!value || typeof value !== 'object') return 'Snapshot was not an object'
  const s = value as Partial<Snapshot>
  if (s.version !== 1) return `Unsupported snapshot version ${String(s.version)}`
  if (!s.season?.id) return 'Snapshot has no season'
  if (!Array.isArray(s.games)) return 'Snapshot has no games array'
  if (!Array.isArray(s.roster)) return 'Snapshot has no roster array'
  return null
}
